import React, { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import BinaModal from '../../../ReactModal';
import ApartmentsGallery from './ApartmentsGallery';
import { prettyNumber } from '../../../../utils/helpers';
import { useImageAlt } from '../../../../hooks/useImageAlt';
import * as S from './styled';

type ApartmentPhoto = {
  f660x496: string;
};

type Apartment = {
  area: number;
  priceFrom: number;
  currency: string;
  rooms: number;
  photos: ApartmentPhoto[];
  repair?: { name: string };
  balconiesCount?: number;
  bathroomsCount?: number;
};

interface ApartmentsModalProps {
  apartment: Apartment;
  isOpen: boolean;
  hide: () => void;
  imgDescription: string;
}

const ApartmentsModal: React.FC<ApartmentsModalProps> = memo(
  ({ apartment, isOpen, hide, imgDescription }) => {
    const { t } = useTranslation();

    const totalPrice = prettyNumber(
      Math.round(apartment.area * apartment.priceFrom)
    );
    const pricePerMeter = prettyNumber(apartment.priceFrom);
    const { currency } = apartment;

    const imgAlt = useImageAlt({
      rooms: apartment.rooms,
      area: apartment.area,
      description: imgDescription
    });

    const photos = apartment.photos.map((photo, i) => (
      <S.GalleryImg key={i} data-cy="apartments-modal-gallery-img">
        <img src={photo.f660x496} alt={imgAlt} />
      </S.GalleryImg>
    ));

    return (
      <BinaModal
        isOpen={isOpen}
        onRequestClose={hide}
        modalId="apartments"
        overlayClassName="modal__overlay-apartments"
        contentClassName="modal__content-apartments"
      >
        <div>
          <S.Title data-cy="apartments-modal-title">
            {t('residences.apartments_title')}
            <S.CloseBtn
              onClick={hide}
              onKeyDown={hide}
              data-cy="apartments-modal-close-btn"
              role="button"
              tabIndex={-1}
              aria-label={t('close')}
            >
              ×
            </S.CloseBtn>
          </S.Title>

          <S.Content>
            <S.Name data-cy="apartments-modal-name">
              {t('rooms.rooms_count', {
                count: apartment.rooms,
                postProcess: 'interval'
              })}
              {t('area_metres', { area: apartment.area })}
            </S.Name>
            <S.Price>
              <Trans
                i18nKey="apartment_price"
                values={{ price: totalPrice, currency }}
              >
                <span className="price-val">{{ totalPrice }}</span>
                <span className="price-cur">{{ currency }}</span>
              </Trans>

              <S.PricePerMeter>
                <Trans
                  i18nKey="apartment_price_per_meter"
                  values={{ price: pricePerMeter, currency }}
                >
                  <span className="price-val">{{ pricePerMeter }}</span>
                  <span className="price-cur">{{ currency }}</span>
                </Trans>
              </S.PricePerMeter>
            </S.Price>
            <S.Description>
              {apartment.repair && (
                <span>
                  {t('apartment_repair', { name: apartment.repair.name })}
                </span>
              )}

              {apartment.balconiesCount && (
                <span>
                  {t('apartment_balcon_count', {
                    count: apartment.balconiesCount
                  })}
                </span>
              )}

              {apartment.bathroomsCount && (
                <span>
                  {t('apartment_bathroom_count', {
                    count: apartment.bathroomsCount
                  })}
                </span>
              )}
            </S.Description>
            <ApartmentsGallery photos={photos} />
          </S.Content>
        </div>
      </BinaModal>
    );
  }
);

ApartmentsModal.displayName = 'ApartmentsModal';

export default ApartmentsModal;
